// ХУК ГОЛОСА ПАНЕЛИ C — связывает контроллер записи (`recorder.js`) с полями шага 3. Г-4.
//
// ⭐ ЗАПИСЬ ПИШЕТ В ТУ КАРТОЧКУ, ГДЕ НАЧАЛАСЬ. Адрес поля (карточка + T1–T3) фиксируется на нажатии;
// писатель получает его явно, а не берёт «текущую» карточку экрана — листнул деку во время «Finishing…»,
// финал всё равно ляжет в своё поле.
//
// ⚠️ ОТРЕЗОК ЗАПИСИ (`voiceText.js`) СЧИТАЕТСЯ ВНУТРИ АПДЕЙТЕРА из СНЯТОГО ДО ВЫЗОВА значения: React
// может позвать апдейтер дважды (StrictMode), и двойной вызов обязан дать тот же результат, а не
// вписать черновик дважды.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createVoice, PHASE } from './recorder.js';
import { createScreenHold } from './wakeLock.js';
import { voicePass } from './transcribeClient.js';
import { startSegment, withDraft, withFinal } from './voiceText.js';

/**
 * @param {object} p
 * @param {(card: number, field: string, fn: (cur: string) => string) => void} p.writeField — писатель поля по адресу
 * @param {(card: number, field: string) => string} p.readField — текущее значение поля
 * @param {number} p.stages — число этапов (для бюджета прохода, Г-3)
 * @param {object} [p.deps] — подмена браузера (тесты)
 */
export function useVoice({ writeField, readField, stages, deps }) {
  const ctrl = useRef(null);
  /** Удержание экрана (строка 82): берётся на нажатии (жест), отпускается, когда голос в покое. */
  const screenHold = useRef(null);
  const target = useRef(null);
  const seg = useRef(null);
  const live = useRef({ writeField, readField, stages });
  live.current = { writeField, readField, stages };
  const [state, setState] = useState({ phase: PHASE.IDLE, field: null, startedAt: null, unavailable: false, limit: false, limitField: null, micOff: null });
  const [where, setWhere] = useState(null); // { card, field } последней записи — для строк под полем
  /**
   * Поля со строкой сбоя (`s517.d7`, кадр `M4·v2`): ключ `карточка:поле`. Ставится, когда кусок сдался, а финал
   * не пришёл (`s995.d1` п. 6); снимается новой записью в этом поле и ручной правкой поля (п. 7). Два поля — две
   * строки. Конверт не трогается (`s929.d2`): это событие экрана, не ответ.
   */
  const [failed, setFailed] = useState(() => new Set());
  const keyOf = (card, field) => `${card}:${field}`;
  const dropFailed = useCallback((card, field) => {
    setFailed((cur) => {
      const k = `${card}:${field}`;
      if (!cur.has(k)) return cur;
      const next = new Set(cur);
      next.delete(k);
      return next;
    });
  }, []);

  const apply = useCallback((step) => {
    const t = target.current;
    if (!t) return;
    const before = seg.current;
    live.current.writeField(t.card, t.field, (cur) => {
      const r = step(cur, before);
      seg.current = r.seg;
      return r.value;
    });
  }, []);

  useEffect(() => {
    const c = createVoice({
      onState: setState,
      onDraft: (_f, text) => apply((cur, s) => withDraft(cur, s, text)),
      onFinal: (_f, text, info) => {
        if (text !== null) { apply((cur, s) => withFinal(cur, s, text)); return; }
        const t = target.current;
        if (info?.hole && t) setFailed((cur) => new Set(cur).add(`${t.card}:${t.field}`));
      },
      context: () => ({ pass: voicePass(), stages: live.current.stages }),
      deps,
    });
    ctrl.current = c;
    const w = createScreenHold({ env: deps?.env });
    screenHold.current = w;
    setState(c.state);
    // Уход с экрана — «стоп и сохранить», а не отмена: финал допишется в своё поле.
    return () => { c.stop(); w.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Голос вернулся в покой (финал пришёл, отказ, сторож) — экран больше не держим.
  useEffect(() => {
    if (state.phase === PHASE.IDLE) screenHold.current?.release();
  }, [state.phase]);

  const begin = (card, field, caret) => {
    const s = ctrl.current?.state;
    if (!s || s.unavailable || s.limit || s.phase !== PHASE.IDLE) return false;
    target.current = { card, field };
    dropFailed(card, field); // новая запись в поле снимает старую строку сбоя
    seg.current = startSegment(live.current.readField(card, field), caret);
    setWhere({ card, field });
    screenHold.current?.hold(); // внутри жеста нажатия
    return true;
  };

  return {
    state,
    where,
    /** Стоит ли у поля строка сбоя. */
    failedAt: (card, field) => failed.has(keyOf(card, field)),
    /** Ручная правка поля снимает его строку сбоя. */
    clearFailed: dropFailed,
    /** Нажатие. `caret` — курсор поля, если оно в фокусе (иначе запись дописывает в конец). */
    press(card, field, caret = null) {
      const s = ctrl.current?.state;
      if (s && (s.phase === PHASE.RECORDING || s.phase === PHASE.ASKING)) {
        if (target.current?.card === card && target.current?.field === field) ctrl.current.press(field);
        return;
      }
      if (begin(card, field, caret)) ctrl.current.press(field);
    },
    release() {
      // ⭐ Удержание экрана — ЕЩЁ РАЗ на отпускании (строка 82, проход Кирилла 20.10): Safari iOS даёт Wake Lock только
      // внутри жеста, а нажатие пальцем жестом не считается — `pointerdown` → `NotAllowedError`, `pointerup` → удержание
      // (замер на симуляторе iOS 26.5, cc-221). Взятие на нажатии оставлено для мыши и клавиатуры.
      const s = ctrl.current?.state;
      if (s && (s.phase === PHASE.RECORDING || s.phase === PHASE.ASKING)) screenHold.current?.hold();
      ctrl.current?.release();
    },
    toggle(card, field, caret = null) {
      const s = ctrl.current?.state;
      if (s && (s.phase === PHASE.RECORDING || s.phase === PHASE.ASKING)) {
        if (target.current?.card === card && target.current?.field === field) ctrl.current.toggle(field);
        return;
      }
      if (begin(card, field, caret)) ctrl.current.toggle(field);
    },
    stop() { ctrl.current?.stop(); },
  };
}
